import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { User, Department, CreateUserRequest } from '../types';
import { Users as UsersIcon, Plus, Edit2, Trash2, CheckCircle, XCircle, Mail, Phone, Building2, Key, Shield, UserCog, Headset } from 'lucide-react';

export const Users: React.FC = () => {
  const { isAdmin, isDepartmentManager, agent } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [formData, setFormData] = useState<CreateUserRequest>({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'AGENT',
    departmentId: '',
  });

  useEffect(() => {
    loadData();
  }, [selectedDepartment, selectedStatus]);

  const loadData = async () => {
    try {
      let departmentIdFilter = selectedDepartment !== 'all' ? selectedDepartment : undefined;

      // Se não é admin, mas é department manager, filtrar apenas usuários do seu departamento
      if (!isAdmin() && isDepartmentManager() && agent?.department?.id) {
        departmentIdFilter = agent.department.id;
      }

      const params: any = {};
      if (departmentIdFilter) params.departmentId = departmentIdFilter;
      if (selectedStatus !== 'all') params.status = selectedStatus;

      const [usersData, depsData] = await Promise.all([
        api.getUsers(params),
        api.getDepartments(),
      ]);
      setUsers(usersData);
      setDepartments(depsData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (user?: User) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        phone: user.phone || '',
        role: user.role as any,
        departmentId: user.department?.id || '',
      });
    } else {
      setEditingUser(null);
      const defaultDepartmentId = !isAdmin() && agent?.department?.id ? agent.department.id : '';
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role: 'AGENT',
        departmentId: defaultDepartmentId,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (editingUser) {
        const updateData: any = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
        };

        if (formData.password) updateData.password = formData.password;
        if (isAdmin()) {
          updateData.role = formData.role;
          updateData.departmentId = formData.departmentId || null;
        }

        await api.updateUser(editingUser.id, updateData);
      } else {
        // Department managers devem criar usuários no seu próprio departamento
        const createData = { ...formData };
        if (!isAdmin() && isDepartmentManager() && agent?.department?.id) {
          createData.departmentId = agent.department.id;
          createData.role = 'AGENT'; // Department managers só podem criar agentes
        }
        await api.createUser(createData);
      }
      await loadData();
      handleCloseModal();
    } catch (error: any) {
      console.error('Failed to save user:', error);
      alert(error.response?.data?.error || 'Failed to save user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await api.updateUser(user.id, { isActive: !user.isActive });
      await loadData();
    } catch (error) {
      console.error('Failed to toggle user:', error);
      alert('Failed to update user');
    }
  };

  const handleOpenDeleteModal = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setUserToDelete(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      await api.deleteUser(userToDelete.id);
      await loadData();
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Falha ao excluir usuário');
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const colors = {
      ADMIN: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      DEPARTMENT_MANAGER: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      AGENT: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
    };

    const labels = {
      ADMIN: 'Admin',
      DEPARTMENT_MANAGER: 'Gerente',
      AGENT: 'Agente',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role as keyof typeof colors]}`}>
        {labels[role as keyof typeof labels]}
      </span>
    );
  };

  const getRoleInfo = (role: string) => {
    const roleInfo = {
      ADMIN: {
        icon: Shield,
        color: 'purple',
        title: 'Administrador do Sistema',
        permissions: [
          'Acesso total ao sistema',
          'Gerenciar todos os departamentos',
          'Criar/editar/excluir qualquer usuário',
          'Modificar configurações globais',
          'Visualizar todas as conversas',
          'Acesso a relatórios completos',
        ],
        note: 'Apenas para departamento "Administração do Sistema"',
      },
      DEPARTMENT_MANAGER: {
        icon: UserCog,
        color: 'blue',
        title: 'Gerente de Departamento',
        permissions: [
          'Gerenciar usuários do seu departamento',
          'Criar e editar agentes',
          'Editar informações do departamento',
          'Visualizar conversas do departamento',
          'Atribuir conversas aos agentes',
          'Acesso a relatórios do departamento',
        ],
        note: 'Permissões limitadas ao seu departamento',
      },
      AGENT: {
        icon: Headset,
        color: 'green',
        title: 'Agente de Atendimento',
        permissions: [
          'Atender conversas do departamento',
          'Visualizar conversas atribuídas',
          'Enviar mensagens aos clientes',
          'Atualizar status de disponibilidade',
          'Gerenciar contatos e tags',
          'Visualizar histórico de conversas',
        ],
        note: 'Foco no atendimento ao cliente',
      },
    };

    return roleInfo[role as keyof typeof roleInfo] || roleInfo.AGENT;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isAdmin() ? 'Usuários' : 'Departamento'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isAdmin() ? 'Gerenciar usuários do sistema' : `Gerenciar funcionários do departamento ${agent?.department?.name || ''}`}
          </p>
        </div>
        {(isAdmin() || isDepartmentManager()) && (
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{isAdmin() ? 'Adicionar Usuário' : 'Adicionar Funcionário'}</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Department Filter */}
          {isAdmin() && departments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Filtrar por Departamento
              </label>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              >
                <option value="all">Todos os Departamentos</option>
                <option value="none">Sem departamento</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Filtrar por Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
            >
              <option value="all">Todos</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
            <UsersIcon className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
            <p className="text-gray-600 dark:text-gray-400">Nenhum usuário encontrado</p>
          </div>
        ) : (
          users.map((user) => (
            <div
              key={user.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 hover:shadow-lg transition"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{user.name}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    {getRoleBadge(user.role)}
                    <span
                      className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${
                        user.isActive
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      {user.isActive ? (
                        <>
                          <CheckCircle className="w-3 h-3" />
                          <span>Ativo</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-3 h-3" />
                          <span>Inativo</span>
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="truncate">{user.email}</span>
                </div>
                {user.phone && (
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                    <Phone className="w-4 h-4 mr-2" />
                    <span>{user.phone}</span>
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <Building2 className="w-4 h-4 mr-2" />
                  <span>{user.department?.name || 'Sem departamento'}</span>
                </div>
                {user.plainPassword && (
                  <div className="flex flex-col space-y-1">
                    <div className="flex items-center text-sm">
                      <Key className="w-4 h-4 mr-2 text-amber-500" />
                      <span className="font-mono bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-1 rounded">
                        {user.plainPassword}
                      </span>
                    </div>
                    {isAdmin() && (
                      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1 text-xs text-amber-700 dark:text-amber-400">
                        ⚠️ Lembre o usuário de alterar esta senha no primeiro acesso
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                {(isAdmin() || (isDepartmentManager() && user.role !== 'ADMIN' && user.role !== 'DEPARTMENT_MANAGER')) && (
                  <>
                    <button
                      onClick={() => handleOpenModal(user)}
                      className="flex-1 px-3 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition flex items-center justify-center space-x-1"
                    >
                      <Edit2 className="w-4 h-4" />
                      <span>Editar</span>
                    </button>
                    {isAdmin() && user.id !== agent?.id && (
                      <>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                        >
                          {user.isActive ? 'Desativar' : 'Ativar'}
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(user)}
                          className="px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {isDepartmentManager() && !isAdmin() && (
                      <>
                        <button
                          onClick={() => handleToggleActive(user)}
                          className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition text-sm"
                        >
                          {user.isActive ? 'Desativar' : 'Ativar'}
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(user)}
                          className="px-3 py-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {editingUser ? (isAdmin() ? 'Editar Usuário' : 'Editar Funcionário') : (isAdmin() ? 'Adicionar Usuário' : 'Adicionar Funcionário')}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="Nome completo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="email@exemplo.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Senha {editingUser && '(deixe em branco para manter a atual)'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  minLength={6}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder={editingUser ? 'Deixe em branco para não alterar' : 'Mínimo 6 caracteres'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Telefone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  placeholder="(00) 00000-0000"
                />
              </div>

              {isAdmin() && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Departamento
                    </label>
                    <select
                      value={formData.departmentId}
                      onChange={(e) => {
                        const newDeptId = e.target.value;
                        const selectedDept = departments.find(d => d.id === newDeptId);
                        const isAdminDept = selectedDept?.name === 'Administração do Sistema';

                        // Se é dept admin, automaticamente define role como ADMIN
                        if (isAdminDept) {
                          setFormData({ ...formData, departmentId: newDeptId, role: 'ADMIN' });
                        }
                        // Se não é dept admin e o role atual é ADMIN, mudar para AGENT
                        else if (!isAdminDept && formData.role === 'ADMIN') {
                          setFormData({ ...formData, departmentId: newDeptId, role: 'AGENT' });
                        } else {
                          setFormData({ ...formData, departmentId: newDeptId });
                        }
                      }}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Nenhum</option>
                      {departments.map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Função *
                    </label>
                    {(() => {
                      const selectedDept = departments.find(d => d.id === formData.departmentId);
                      const isAdminDept = selectedDept?.name === 'Administração do Sistema';

                      return (
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                          required
                          disabled={isAdminDept}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none disabled:opacity-75 disabled:cursor-not-allowed"
                        >
                          {isAdminDept ? (
                            <option value="ADMIN">Administrador do Sistema</option>
                          ) : (
                            <>
                              <option value="AGENT">Agente de Atendimento</option>
                              <option value="DEPARTMENT_MANAGER">Gerente de Departamento</option>
                              <option value="ADMIN" disabled>
                                Administrador do Sistema (apenas para Administração do Sistema)
                              </option>
                            </>
                          )}
                        </select>
                      );
                    })()}
                    {departments.find(d => d.id === formData.departmentId)?.name === 'Administração do Sistema' && (
                      <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                        ℹ️ Departamento de Administração do Sistema aceita apenas Administradores
                      </p>
                    )}

                    {/* Role Information Card */}
                    {(() => {
                      const info = getRoleInfo(formData.role);
                      const Icon = info.icon;
                      const colorClasses = {
                        purple: {
                          bg: 'bg-purple-50 dark:bg-purple-900/20',
                          border: 'border-purple-200 dark:border-purple-800',
                          text: 'text-purple-700 dark:text-purple-400',
                          icon: 'text-purple-600 dark:text-purple-400',
                        },
                        blue: {
                          bg: 'bg-blue-50 dark:bg-blue-900/20',
                          border: 'border-blue-200 dark:border-blue-800',
                          text: 'text-blue-700 dark:text-blue-400',
                          icon: 'text-blue-600 dark:text-blue-400',
                        },
                        green: {
                          bg: 'bg-green-50 dark:bg-green-900/20',
                          border: 'border-green-200 dark:border-green-800',
                          text: 'text-green-700 dark:text-green-400',
                          icon: 'text-green-600 dark:text-green-400',
                        },
                      };
                      const colors = colorClasses[info.color as keyof typeof colorClasses];

                      return (
                        <div className={`mt-3 p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
                          <div className="flex items-start space-x-2 mb-2">
                            <Icon className={`w-5 h-5 ${colors.icon} flex-shrink-0 mt-0.5`} />
                            <div className="flex-1">
                              <h4 className={`font-medium text-sm ${colors.text}`}>{info.title}</h4>
                              <p className={`text-xs ${colors.text} opacity-90 mt-1 italic`}>{info.note}</p>
                            </div>
                          </div>
                          <ul className={`text-xs ${colors.text} space-y-1 ml-7`}>
                            {info.permissions.map((perm, idx) => (
                              <li key={idx} className="flex items-start">
                                <span className="mr-1.5">•</span>
                                <span>{perm}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                  </div>
                </>
              )}

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {isSaving && (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>{isSaving ? 'Salvando...' : (editingUser ? 'Atualizar' : 'Criar')}</span>
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Confirmar Exclusão
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Tem certeza que deseja excluir permanentemente o usuário <span className="font-semibold">{userToDelete.name}</span>?
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mb-6">
              ⚠️ Esta ação não pode ser desfeita!
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isDeleting && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>{isDeleting ? 'Excluindo...' : 'Sim, Excluir'}</span>
              </button>
              <button
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
